// Marketing hooks (marketing) — the composed Marketing-overview read + the press-hits feed
// CRUD, hand-written over the generated client via the tenant-keyed wrappers. Keys are
// ['band', bandId, 'marketing-overview'] and ['band', bandId, 'press-hits'] so a band switch scorches them AND the
// `sync-completed` plane-B signal repaints the overview live (invalidationRegistry). The overview is served straight
// from the analytics.* projections + artist.releases — NO vendor call on this read path, and its paid KPIs
// null-degrade to '—' until lands account-level ads insights (the FE status strip uses the REAL Meta connection
// state via useConnections). The whole surface is releases:read (the audience), money-free re: the seat axis.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

export type MarketingOverview = components["schemas"]["MarketingOverview"];
export type MarketingHero = components["schemas"]["MarketingHero"];
export type MarketingKpis = components["schemas"]["MarketingKpis"];
export type BoostCard = components["schemas"]["BoostCard"];
export type MarketingReleaseRow = components["schemas"]["MarketingReleaseRow"];
export type DspDistributionRow = components["schemas"]["DspDistributionRow"];
export type MarketingConfigSlot = components["schemas"]["MarketingConfigSlot"];
export type PressHitDto = components["schemas"]["PressHitDto"];
export type PressHitsRead = components["schemas"]["PressHitsRead"];
export type PressHitUpsertRequest = components["schemas"]["PressHitUpsertRequest"];
export type BoostRecsView = components["schemas"]["BoostRecsView"];
export type BoostRec = components["schemas"]["BoostRec"];

/** GET the composed Marketing overview (releases:read). `staleTime` ~60s per the stats domain.*/
export function useMarketingOverview() {
  const { api } = useApp();
  return useBandQuery<MarketingOverview>(
    ["marketing-overview"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/marketing/overview", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 60_000 },
  );
}

/**
 * GET "Recommended for an ad" (releases:read) — the band's own Instagram posts scored by
 * reach × engagement-rate × velocity.
 *
 * Its own query rather than a field on the overview because the two answer different questions and the section
 * has its own loading state: the spec is explicit that while scoring is in flight the section shows SKELETONS
 * and not its empty card ("the 'no recommendations' card read as broken"). Folding it into the overview would
 * tie that state to the hero's.
 */
export function useBoostRecs() {
  const { api } = useApp();
  return useBandQuery<BoostRecsView>(
    ["boost-recs"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/marketing/boost-recs", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { staleTime: 60_000 },
  );
}

/** GET the press-hits feed (releases:read). */
export function usePressHits() {
  const { api } = useApp();
  return useBandQuery<PressHitsRead>(
    ["press-hits"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/press-hits", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  );
}

/** POST a press-hit — creates when `hitId` is absent, edits the existing row when present (the editable-rows
 * upsert; no PUT). Invalidates the feed on success. */
export function useUpsertPressHit() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<PressHitDto, PressHitUpsertRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/press-hits", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "press-hits"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save the press hit")),
    },
  );
}

/** DELETE a press-hit (idempotent 204). Invalidates the feed on success. */
export function useDeletePressHit() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { hitId: string }>(
    async (band, { hitId }) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/press-hits/{hitId}", {
        params: { path: { bandId: band, hitId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "press-hits"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't delete the press hit")),
    },
  );
}
